import { UserButton, useAuth, useUser } from "@clerk/react"
import { useRemoteAccessDecision } from "../auth/useRemoteAccess"
import {
  CHALLENGE_STORE_KEY,
  CHALLENGE_STORE_VERSION,
  ChallengeStoreSchema,
  EMPTY_CHALLENGE_STORE,
} from "../challenges/storage"
import { useStoredState } from "../hooks/useStoredState"
import { useLocale } from "../i18n/locale"
import { type AppLocale, supportedLocales } from "../i18n/messages"
import { useI18n } from "../i18n/useI18n"
import { InvitePanel } from "../invites/InvitePanel"
import { summarizeSafetyStats } from "../moderation/metrics"
import {
  EMPTY_PLAYER_REPORT_STORE,
  PLAYER_REPORT_STORAGE_KEY,
  PLAYER_REPORT_STORAGE_VERSION,
  PlayerReportStoreSchema,
  updatePlayerReportStatus,
} from "../moderation/reports"
import {
  deriveAccountStanding,
  EMPTY_MODERATION_INCIDENT_STORE,
  MODERATION_INCIDENT_STORAGE_KEY,
  MODERATION_INCIDENT_STORAGE_VERSION,
  ModerationIncidentStoreSchema,
  updateModerationIncidentStatus,
} from "../moderation/storage"
import { ProfileBootstrap } from "../profile/ProfileBootstrap"
import {
  EMPTY_PLAYER_PROFILE_STORE,
  PLAYER_PROFILES_STORAGE_KEY,
  PLAYER_PROFILES_STORAGE_VERSION,
  PlayerProfileStoreStateSchema,
} from "../profile/storage"
import { getProfileByEmail, normalizeEmail } from "../profile/validation"
import { summarizeAdminUsage } from "../stats/admin"
import { SystemBattleBotsPanel } from "../system/SystemBattleBotsPanel"

function formatDateTime(
  dateString: string | null,
  locale: string,
  fallback: string,
) {
  if (!dateString) return fallback
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(dateString))
}

function LanguageSettingsPanel() {
  const { locale, setLocale } = useLocale()
  const { t } = useI18n()

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">{t("settings.eyebrow.account")}</p>
          <h2>{t("settings.language.title")}</h2>
          <p className="meta">{t("settings.language.description")}</p>
        </div>
      </div>
      <label className="field">
        <span>{t("settings.language.label")}</span>
        <select
          className="text-input"
          onChange={(event) =>
            setLocale(event.currentTarget.value as AppLocale)
          }
          value={locale}
        >
          {supportedLocales.map((option) => (
            <option key={option} value={option}>
              {t(`settings.language.option.${option}` as const)}
            </option>
          ))}
        </select>
      </label>
    </section>
  )
}

function AccountContent() {
  const { locale, t } = useI18n()
  const { user } = useUser()
  const { access, isChecking } = useRemoteAccessDecision()
  const email = user?.primaryEmailAddress?.emailAddress ?? null
  const accountName =
    user?.fullName?.trim() ||
    [user?.firstName?.trim(), user?.lastName?.trim()].filter(Boolean).join(" ")
  const [profiles] = useStoredState(
    PLAYER_PROFILES_STORAGE_KEY,
    PlayerProfileStoreStateSchema,
    {
      defaultValue: EMPTY_PLAYER_PROFILE_STORE,
      version: PLAYER_PROFILES_STORAGE_VERSION,
    },
  )
  const [moderationIncidents] = useStoredState(
    MODERATION_INCIDENT_STORAGE_KEY,
    ModerationIncidentStoreSchema,
    {
      defaultValue: EMPTY_MODERATION_INCIDENT_STORE,
      version: MODERATION_INCIDENT_STORAGE_VERSION,
    },
  )
  if (!email) {
    return (
      <section className="panel panel-danger">
        <h2>{t("settings.emailRequiredTitle")}</h2>
        <p>{t("settings.emailRequiredBody")}</p>
      </section>
    )
  }

  if (isChecking || !access) {
    return (
      <section className="panel">
        <h2>{t("auth.loadingTitle")}</h2>
        <p>{t("auth.loadingBody")}</p>
      </section>
    )
  }

  if (access === "missing_email" || access === "denied") {
    return (
      <section className="panel panel-danger">
        <h2>{t("account.accessRequiredTitle")}</h2>
        <p>{t("account.accessRequiredBody")}</p>
      </section>
    )
  }

  const existingProfile = getProfileByEmail(profiles, normalizeEmail(email))
  const standing = deriveAccountStanding(
    moderationIncidents,
    normalizeEmail(email),
  )

  return (
    <>
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="eyebrow">{t("settings.eyebrow.account")}</p>
            <h2>{t("account.identity.title")}</h2>
          </div>
          <UserButton />
        </div>
        <dl className="status-list">
          {accountName ? (
            <div>
              <dt>{t("common.name")}</dt>
              <dd>{accountName}</dd>
            </div>
          ) : null}
          <div>
            <dt>{t("common.signedInEmail")}</dt>
            <dd>{email}</dd>
          </div>
          <div>
            <dt>{t("auth.accessClass")}</dt>
            <dd>
              {access === "bootstrap_admin"
                ? t("settings.accessClass.bootstrapAdmin")
                : t("settings.accessClass.invitedPlayer")}
            </dd>
          </div>
          {existingProfile && (
            <div>
              <dt>{t("common.gamerTag")}</dt>
              <dd>{existingProfile.gamerTag}</dd>
            </div>
          )}
        </dl>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="eyebrow">{t("common.status")}</p>
            <h2>{t("settings.accountStanding.title")}</h2>
          </div>
          <span
            className={`status-badge ${
              standing.level === "good"
                ? "status-badge-ok"
                : standing.level === "warned"
                  ? "status-badge-warn"
                  : "status-badge-danger"
            }`}
          >
            {t(`settings.accountStanding.level.${standing.level}` as const)}
          </span>
        </div>
        <dl className="status-list">
          <div>
            <dt>{t("settings.accountStanding.activeFlags")}</dt>
            <dd>{standing.activeCount}</dd>
          </div>
          <div>
            <dt>{t("settings.accountStanding.latestAttempt")}</dt>
            <dd>
              {formatDateTime(
                standing.latestIncidentAt,
                locale,
                t("common.none"),
              )}
            </dd>
          </div>
        </dl>
      </section>

      <ProfileBootstrap access={access} alwaysShowForm email={email} />
    </>
  )
}

function AdminContent() {
  const { locale, t } = useI18n()
  const { user } = useUser()
  const email = user?.primaryEmailAddress?.emailAddress ?? null
  const [profiles] = useStoredState(
    PLAYER_PROFILES_STORAGE_KEY,
    PlayerProfileStoreStateSchema,
    {
      defaultValue: EMPTY_PLAYER_PROFILE_STORE,
      version: PLAYER_PROFILES_STORAGE_VERSION,
    },
  )
  const [moderationIncidents, setModerationIncidents] = useStoredState(
    MODERATION_INCIDENT_STORAGE_KEY,
    ModerationIncidentStoreSchema,
    {
      defaultValue: EMPTY_MODERATION_INCIDENT_STORE,
      version: MODERATION_INCIDENT_STORAGE_VERSION,
    },
  )
  const [challenges] = useStoredState(
    CHALLENGE_STORE_KEY,
    ChallengeStoreSchema,
    {
      defaultValue: EMPTY_CHALLENGE_STORE,
      version: CHALLENGE_STORE_VERSION,
    },
  )
  const [playerReports, setPlayerReports] = useStoredState(
    PLAYER_REPORT_STORAGE_KEY,
    PlayerReportStoreSchema,
    {
      defaultValue: EMPTY_PLAYER_REPORT_STORE,
      version: PLAYER_REPORT_STORAGE_VERSION,
    },
  )

  if (!email) {
    return (
      <section className="panel panel-danger">
        <h2>{t("settings.emailRequiredTitle")}</h2>
        <p>{t("settings.emailRequiredBody")}</p>
      </section>
    )
  }

  const pendingModerationIncidents = moderationIncidents.filter(
    (incident) => incident.status === "pending",
  )
  const pendingPlayerReports = playerReports.filter(
    (report) => report.status === "pending",
  )
  const allProfiles = Object.values(profiles)
  const usageStats = summarizeAdminUsage(allProfiles, Object.values(challenges))
  const safetyStats = summarizeSafetyStats(moderationIncidents, playerReports)

  return (
    <>
      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="eyebrow">{t("common.players")}</p>
            <h2>{t("settings.socialTelemetry.title")}</h2>
            <p className="meta">{t("settings.socialTelemetry.description")}</p>
          </div>
        </div>
        <dl className="status-list">
          <div>
            <dt>{t("settings.socialTelemetry.profiles")}</dt>
            <dd>
              {t("settings.socialTelemetry.profilesSummary", {
                total: usageStats.totalProfiles,
                players: usageStats.playerProfiles,
              })}
            </dd>
          </div>
          <div>
            <dt>{t("settings.socialTelemetry.admins")}</dt>
            <dd>{usageStats.adminProfiles}</dd>
          </div>
          <div>
            <dt>{t("settings.socialTelemetry.challenges")}</dt>
            <dd>
              {t("settings.socialTelemetry.challengesSummary", {
                total: usageStats.totalChallenges,
                pending: usageStats.pendingChallenges,
              })}
            </dd>
          </div>
          <div>
            <dt>{t("settings.socialTelemetry.acceptedChallenges")}</dt>
            <dd>{usageStats.acceptedChallenges}</dd>
          </div>
          <div>
            <dt>{t("settings.socialTelemetry.activePlayers")}</dt>
            <dd>{usageStats.activePlayers}</dd>
          </div>
          <div>
            <dt>{t("settings.socialTelemetry.latestChallenge")}</dt>
            <dd>
              {formatDateTime(
                usageStats.latestChallengeAt,
                locale,
                t("common.none"),
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="eyebrow">{t("common.status")}</p>
            <h2>{t("settings.safetyTelemetry.title")}</h2>
            <p className="meta">{t("settings.safetyTelemetry.description")}</p>
          </div>
        </div>
        <dl className="status-list">
          <div>
            <dt>{t("settings.safetyTelemetry.incidents")}</dt>
            <dd>
              {t("settings.safetyTelemetry.incidentsSummary", {
                total: safetyStats.incidentTotals.total,
                pending: safetyStats.incidentTotals.pending,
              })}
            </dd>
          </div>
          <div>
            <dt>{t("settings.safetyTelemetry.reports")}</dt>
            <dd>
              {t("settings.safetyTelemetry.reportsSummary", {
                total: safetyStats.reportTotals.total,
                pending: safetyStats.reportTotals.pending,
              })}
            </dd>
          </div>
          <div>
            <dt>{t("settings.safetyTelemetry.flaggedAccounts")}</dt>
            <dd>{safetyStats.uniqueActors}</dd>
          </div>
          <div>
            <dt>{t("settings.safetyTelemetry.uniqueReporters")}</dt>
            <dd>{safetyStats.uniqueReporterTags}</dd>
          </div>
          <div>
            <dt>{t("settings.safetyTelemetry.uniqueReportedPlayers")}</dt>
            <dd>{safetyStats.uniqueReportedTags}</dd>
          </div>
          <div>
            <dt>{t("settings.safetyTelemetry.latestEvent")}</dt>
            <dd>
              {formatDateTime(
                safetyStats.latestEventAt,
                locale,
                t("common.none"),
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="eyebrow">{t("common.status")}</p>
            <h2>{t("settings.adminReview.title")}</h2>
            <p className="meta">{t("settings.adminReview.description")}</p>
          </div>
          <span className="status-badge status-badge-muted">
            {t("settings.adminReview.pendingCount", {
              count: pendingModerationIncidents.length,
            })}
          </span>
        </div>

        {pendingModerationIncidents.length === 0 ? (
          <p className="meta">{t("settings.adminReview.empty")}</p>
        ) : (
          <div className="moderation-review-list">
            {pendingModerationIncidents.map((incident) => (
              <article className="moderation-review-item" key={incident.id}>
                <div className="moderation-review-copy">
                  <strong>
                    {t(`settings.adminReview.type.${incident.type}` as const)}
                  </strong>
                  <p className="meta">
                    {t("settings.adminReview.value", {
                      value: incident.value,
                    })}
                  </p>
                  <p className="meta">
                    {t("settings.adminReview.actor", {
                      actor:
                        incident.actorEmail ??
                        t("settings.adminReview.unknownActor"),
                    })}
                  </p>
                  <p className="meta">
                    {t("settings.adminReview.created", {
                      createdAt: formatDateTime(
                        incident.createdAt,
                        locale,
                        t("common.none"),
                      ),
                    })}
                  </p>
                </div>
                <div className="moderation-review-actions">
                  <button
                    className="secondary-button"
                    onClick={() =>
                      setModerationIncidents((previous) =>
                        updateModerationIncidentStatus(
                          previous,
                          incident.id,
                          "confirmed",
                        ),
                      )
                    }
                    type="button"
                  >
                    {t("common.confirm")}
                  </button>
                  <button
                    className="ghost-icon-button"
                    onClick={() =>
                      setModerationIncidents((previous) =>
                        updateModerationIncidentStatus(
                          previous,
                          incident.id,
                          "dismissed",
                        ),
                      )
                    }
                    type="button"
                  >
                    {t("common.dismiss")}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="eyebrow">{t("common.status")}</p>
            <h2>{t("settings.reports.title")}</h2>
            <p className="meta">{t("settings.reports.description")}</p>
          </div>
          <span className="status-badge status-badge-muted">
            {t("settings.adminReview.pendingCount", {
              count: pendingPlayerReports.length,
            })}
          </span>
        </div>

        {pendingPlayerReports.length === 0 ? (
          <p className="meta">{t("settings.reports.empty")}</p>
        ) : (
          <div className="moderation-review-list">
            {pendingPlayerReports.map((report) => (
              <article className="moderation-review-item" key={report.id}>
                <div className="moderation-review-copy">
                  <strong>
                    {t("settings.reports.headline", {
                      reported: report.reportedTag,
                      reporter: report.reporterTag,
                    })}
                  </strong>
                  <p className="meta">
                    {t("settings.reports.reason", {
                      reason: report.reason,
                    })}
                  </p>
                  <p className="meta">
                    {t("settings.adminReview.created", {
                      createdAt: formatDateTime(
                        report.createdAt,
                        locale,
                        t("common.none"),
                      ),
                    })}
                  </p>
                </div>
                <div className="moderation-review-actions">
                  <button
                    className="secondary-button"
                    onClick={() =>
                      setPlayerReports((previous) =>
                        updatePlayerReportStatus(
                          previous,
                          report.id,
                          "reviewed",
                        ),
                      )
                    }
                    type="button"
                  >
                    {t("settings.reports.markReviewed")}
                  </button>
                  <button
                    className="ghost-icon-button"
                    onClick={() =>
                      setPlayerReports((previous) =>
                        updatePlayerReportStatus(
                          previous,
                          report.id,
                          "dismissed",
                        ),
                      )
                    }
                    type="button"
                  >
                    {t("common.dismiss")}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <InvitePanel />
      <SystemBattleBotsPanel />
    </>
  )
}

export function AccountPage() {
  const { t } = useI18n()
  const { isLoaded, userId } = useAuth()

  if (!isLoaded) {
    return (
      <section className="panel management-panel">
        <div className="page-header">
          <div>
            <p className="eyebrow">{t("settings.eyebrow.account")}</p>
            <h2>{t("account.title")}</h2>
          </div>
        </div>
        <LanguageSettingsPanel />
        <p className="meta">{t("account.loading")}</p>
      </section>
    )
  }

  if (!userId) {
    return (
      <section className="panel management-panel">
        <div className="page-header">
          <div>
            <p className="eyebrow">{t("settings.eyebrow.account")}</p>
            <h2>{t("account.title")}</h2>
          </div>
        </div>
        <LanguageSettingsPanel />
        <p>{t("account.signInRequired")}</p>
      </section>
    )
  }

  return (
    <section className="panel management-panel">
      <div className="page-header">
        <div>
          <p className="eyebrow">{t("settings.eyebrow.account")}</p>
          <h2>{t("account.title")}</h2>
          <p className="lede table-lede">{t("account.lede")}</p>
        </div>
      </div>

      <LanguageSettingsPanel />
      <AccountContent />
    </section>
  )
}

export function AdminPage() {
  const { t } = useI18n()
  const { isLoaded, userId } = useAuth()
  const { access, isChecking } = useRemoteAccessDecision()

  if (!isLoaded || isChecking) {
    return (
      <section className="panel management-panel">
        <div className="page-header">
          <div>
            <p className="eyebrow">{t("common.status")}</p>
            <h2>{t("admin.title")}</h2>
          </div>
        </div>
        <p className="meta">{t("settings.loading")}</p>
      </section>
    )
  }

  if (!userId) {
    return (
      <section className="panel management-panel">
        <div className="page-header">
          <div>
            <p className="eyebrow">{t("common.status")}</p>
            <h2>{t("admin.title")}</h2>
          </div>
        </div>
        <p>{t("account.signInRequired")}</p>
      </section>
    )
  }

  if (access !== "bootstrap_admin") {
    return (
      <section className="panel management-panel">
        <div className="page-header">
          <div>
            <p className="eyebrow">{t("common.status")}</p>
            <h2>{t("admin.title")}</h2>
          </div>
        </div>
        <section className="panel panel-danger">
          <h2>{t("admin.accessRequiredTitle")}</h2>
          <p>{t("admin.accessRequiredBody")}</p>
        </section>
      </section>
    )
  }

  return (
    <section className="panel management-panel">
      <div className="page-header">
        <div>
          <p className="eyebrow">{t("common.status")}</p>
          <h2>{t("admin.title")}</h2>
          <p className="lede table-lede">{t("admin.lede")}</p>
        </div>
      </div>

      <AdminContent />
    </section>
  )
}
